import { FC, useEffect, useState } from "react";

import { Button, Radio, RadioChangeEvent, Space, Switch } from "antd";
import { ClearOutlined } from "@ant-design/icons";

import styles from "./Report.module.scss";

import { IExamination } from "@/models/Examination";
import { ERROR_500_MESSAGE } from "@/api/constants";
import { ExaminationsApi } from "@/api";
import {
  clearExaminationData,
  setChosenExamination,
  setExaminationStatus,
} from "@/store/reducers/currentData/currentDataSlice";
import { activateExamination } from "@/store/reducers/currentData/asyncActionCreators";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  Correction,
  CorrectionForm,
  ExaminationForm,
  ReportActions,
} from "@/modules/Report/components";
import useExaminationForm from "@/modules/Report/hooks/useExaminationForm";
import { ChosenReportTable, StandStreamTable } from "@/components";
import useDeleteExamination from "@/modules/Report/hooks/useDeleteExamination";
import { ExaminationStatus } from "@/root/types";

const Report: FC = () => {
  const userType = useAppSelector((state) => state.userReducer.userType);
  const {
    m15: isExamination,
    activeExamination,
    chosenExamination,
    examinationStatus,
  } = useAppSelector((state) => state.currentDataReducer);
  const { form, clearForm, setExaminationFields } = useExaminationForm();
  const dispatch = useAppDispatch();
  const [autoScroll, setAutoScroll] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [period, setPeriod] = useState<10 | 60>(60);
  const { loading: deleteLoading, handleDelete: handleDeleteExamination } =
    useDeleteExamination();

  useEffect(() => {
    if (isExamination && activeExamination) {
      setExaminationFields(activeExamination);
    }
  }, [isExamination, activeExamination]);

  const clearTable = () => {
    dispatch(clearExaminationData());
  };

  const handleChooseExamination = (examination: IExamination) => {
    createError && setCreateError("");
    dispatch(setExaminationStatus(ExaminationStatus.default));
    dispatch(clearExaminationData());
    if (activeExamination?.examId === examination.examId) {
      dispatch(setChosenExamination(null));
    } else {
      dispatch(setChosenExamination(examination));
    }
    setExaminationFields(examination);
  };

  const handleCopy = () => {
    if (!chosenExamination) return;
    createError && setCreateError("");
    setExaminationFields(chosenExamination);
    dispatch(setChosenExamination(null));
    clearTable();
  };

  const handleNewExam = () => {
    dispatch(setExaminationStatus(ExaminationStatus.default));
    dispatch(clearExaminationData());
    dispatch(setChosenExamination(null));
    clearForm();
  };

  const handleSave = () => {
    form.submit();
  };

  const handleDelete = async () => {
    if (chosenExamination?.status === "new") {
      await handleDeleteExamination(chosenExamination.examId);
      dispatch(setChosenExamination(null));
      clearForm();
    }
  };

  const handleActivate = () => {
    if (chosenExamination) {
      dispatch(activateExamination(chosenExamination.examId));
      dispatch(setChosenExamination(null));
    }
  };

  const handleDeactivate = () => {
    if (activeExamination && !isExamination) {
      dispatch(activateExamination(null));
      activeExamination && dispatch(setChosenExamination(activeExamination));
    }
  };

  const examinationFormValues = useAppSelector(
    (state) => state.examinationForm
  );

  const handleSubmit = async () => {
    try {
      setCreateLoading(true);
      setCreateError("");
      let examination;
      if (chosenExamination?.status === "new") {
        examination = await ExaminationsApi.editExamination(
          chosenExamination.examId,
          examinationFormValues
        );
      } else {
        examination = await ExaminationsApi.createExamination(
          examinationFormValues
        );
      }

      clearTable();
      dispatch(setChosenExamination({ ...examination, status: "new" }));
    } catch (e: any) {
      setCreateError(e.response?.data?.message || ERROR_500_MESSAGE);
    } finally {
      setCreateLoading(false);
    }
  };

  const handlePeriodChange = (event: RadioChangeEvent) => {
    setPeriod(event.target.value);
  };

  const formReadOnly =
    isExamination ||
    chosenExamination?.status === "finished" ||
    examinationStatus === ExaminationStatus.finished;
  const isAdmin = userType === "admin";

  return (
    <div>
      <ReportActions
        isFinished={examinationStatus === ExaminationStatus.finished}
        createLoading={createLoading}
        chosenExamination={chosenExamination}
        onChooseExamination={handleChooseExamination}
        onCopy={handleCopy}
        onSave={handleSave}
        onNewExam={handleNewExam}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        deleteLoading={deleteLoading}
        onDelete={handleDelete}
      />
      <div className={styles.examination}>
        <ExaminationForm
          readonly={formReadOnly}
          error={createError}
          form={form}
          onSubmit={handleSubmit}
        />
        {isAdmin && (
          <>
            {chosenExamination?.correction ? (
              <CorrectionForm
                readonly={true}
                values={chosenExamination.correction}
              />
            ) : (
              <Correction readonly={formReadOnly} />
            )}
          </>
        )}
        <Space className={styles.tableActions}>
          {isAdmin && chosenExamination?.status === "finished" && (
            <Radio.Group value={period} onChange={handlePeriodChange}>
              <Radio value={10}>10s</Radio>
              <Radio value={60}>60s</Radio>
            </Radio.Group>
          )}
          <Button icon={<ClearOutlined />} onClick={clearTable}>
            Clear
          </Button>
          <Space>
            <Switch checked={autoScroll} onChange={setAutoScroll} />
            {autoScroll ? "Auto scroll ON" : "Auto scroll OFF"}
          </Space>
        </Space>
      </div>
      {chosenExamination?.status === ExaminationStatus.finished ? (
        <ChosenReportTable examId={chosenExamination.examId} period={period} />
      ) : (
        <StandStreamTable isAutoScroll={autoScroll} />
      )}
    </div>
  );
};

export default Report;
